import React, { useRef, useEffect, useMemo } from 'react';
import 'plyr/dist/plyr.css';
import styled from 'styled-components';
import useVideoPlayer from '../videos/useVideoPlayer';

const VideoContainer = styled.div`
  background: var(--cc_color_brand_secondary);
  height: auto;
  width: 100%;
  position: relative;
  padding-bottom: 56.25%; // Creates space for 16:9 video
  border-radius: var(--cc_size_border_radius_l) var(--cc_size_border_radius_l) 0
    0;

  .plyr__poster {
    background-color: transparent;
    border-radius: inherit;
    z-index: -1 !important;
  }

  .plyr--video {
    border-radius: inherit;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .plyr.plyr--stopped .plyr__controls {
    display: none;
  }
`;

const VideoPlayer = styled.video`
  height: auto;
  width: 100%;
  border-radius: inherit;
`;

const StyledIcon = styled.i`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
`;

const PlayButton = styled.div<{ isVisible: boolean }>`
  height: 74px;
  width: 74px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  box-shadow: 0px 5px 15px var(--cc_color_depth_1);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  transition: all ease-in-out 150ms;
  border: none;
  background-color: var(--cc_color_background_1);
  opacity: ${(props) => (props.isVisible ? 1 : 0)};

  &:hover {
    background-color: var(--cc_color_background_3);
    transition: 0.15s ease-in-out;
  }
`;

const playerConfig = {
  captions: { active: true, update: true, language: 'en' },
  controls: [
    'restart',
    'play',
    'progress',
    'current-time',
    'duration',
    'mute',
    'volume',
    'fullscreen',
    'settings',
  ],
};

const PromoModalVideoPlayer = ({
  src,
  youtubeSrc,
  poster,
  style,
  autoplay = false,
}: {
  src?: string;
  youtubeSrc?: string;
  poster?: string;
  style?: React.CSSProperties;
  autoplay?: boolean;
}) => {
  const videoRef = useRef();

  const config = useMemo(() => {
    return {
      autoplay,
      ...playerConfig,
    };
  }, [autoplay]);

  const { isPlaying, isPaused, togglePlay, setSource } = useVideoPlayer(
    videoRef,
    config,
  );

  useEffect(() => {
    if (src) {
      setSource(src);
    }
  }, [src, setSource]);

  const renderYoutubePlayer = () => {
    return (
      <div
        id="plyr-player"
        data-plyr-provider="youtube"
        data-plyr-embed-id={youtubeSrc}
        ref={videoRef}
      />
    );
  };

  const renderVideoPlayer = () => {
    return <VideoPlayer poster={poster} ref={videoRef} controls />;
  };

  return (
    <VideoContainer style={style} onClick={togglePlay}>
      {youtubeSrc ? renderYoutubePlayer() : renderVideoPlayer()}
      <PlayButton isVisible={!isPlaying || isPaused}>
        <StyledIcon className="mdi mdi-play" />
      </PlayButton>
    </VideoContainer>
  );
};

export default PromoModalVideoPlayer;
